import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './FarmhouseVillas.css';
import { FaChevronLeft, FaChevronRight, FaMapMarkerAlt, FaHome, FaLayerGroup, FaRuler } from 'react-icons/fa';

export default function FarmhouseVillas() {
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [cardWidth, setCardWidth] = useState(410);

  // Calculate card width on mount and resize
  useEffect(() => {
    const calculateCardWidth = () => {
      if (window.innerWidth <= 480) {
        setCardWidth(315);
      } else if (window.innerWidth <= 768) {
        setCardWidth(340);
      } else if (window.innerWidth <= 1024) {
        setCardWidth(370);
      } else {
        setCardWidth(410);
      }
    };

    calculateCardWidth();
    window.addEventListener('resize', calculateCardWidth);
    return () => window.removeEventListener('resize', calculateCardWidth);
  }, []);

  // Farmhouse & Villas property data
  const properties = [
    {
      id: 1,
      slug: 'chattarpur-luxury-farmhouse',
      title: 'Luxury Farmhouse',
      location: 'Chattarpur, New Delhi',
      type: 'Farmhouse',
      builder: 'Private Estate',
      floors: 'Ground + 1st Floor',
      area: '5000 sq.yd',
      category: 'Farmhouse',
      image: 'https://images.pexels.com/photos/1396122/pexels-photo-1396122.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 2,
      slug: 'gurgaon-modern-villa',
      title: 'Modern Villa',
      location: 'Gurgaon, Haryana',
      type: 'Villa',
      builder: 'DLF Villas',
      floors: 'Multi-Level',
      area: '8000 sq.ft',
      category: 'Villa',
      image: 'https://images.pexels.com/photos/1370704/pexels-photo-1370704.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 3,
      slug: 'sohna-countryside-farmhouse',
      title: 'Countryside Farmhouse',
      location: 'Sohna Road, Gurgaon',
      type: 'Farmhouse',
      builder: 'Private Developer',
      floors: 'Ground Floor',
      area: '10000 sq.yd',
      category: 'Farmhouse',
      image: 'https://images.pexels.com/photos/259588/pexels-photo-259588.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 4,
      slug: 'kapashera-premium-villa',
      title: 'Premium Villa',
      location: 'Kapashera, New Delhi',
      type: 'Villa',
      builder: 'Prestige Villas',
      floors: 'Ground + 2 Floors',
      area: '6000 sq.ft',
      category: 'Villa',
      image: 'https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 5,
      slug: 'mehrauli-heritage-farmhouse',
      title: 'Heritage Farmhouse',
      location: 'Mehrauli, New Delhi',
      type: 'Farmhouse',
      builder: 'Heritage Estate',
      floors: 'Ground + 1st Floor',
      area: '7000 sq.yd',
      category: 'Farmhouse',
      image: 'https://images.pexels.com/photos/1974596/pexels-photo-1974596.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      id: 6,
      slug: 'ghitorni-luxury-villa',
      title: 'Luxury Villa',
      location: 'Ghitorni, Delhi',
      type: 'Villa',
      builder: 'Godrej Villas',
      floors: 'Multi-Level',
      area: '7500 sq.ft',
      category: 'Villa',
      image: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const scroll = (direction) => {
    const container = scrollRef.current;
    
    if (direction === 'left') {
      container.scrollBy({ left: -cardWidth, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: cardWidth, behavior: 'smooth' });
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <>
      <section className="farmhouse-section">
        <div className="farmhouse-container">
          
          {/* Header */}
          <div className="section-header">
            <h2>Latest Farmhouse & Villas</h2>
            <p>Explore our collection of luxurious farmhouses and elegant villas</p>
          </div>

          {/* Properties Slider */}
          <div className="slider-wrapper">
            <button 
              className="slider-btn slider-btn-left" 
              onClick={() => scroll('left')}
              aria-label="Scroll Left"
            >
              <FaChevronLeft />
            </button>

            <div 
              className="properties-slider" 
              ref={scrollRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              {properties.map((property, index) => (
                <Link 
                  to={`/properties/farmhouse-villas/${property.slug}`}
                  key={property.id}
                  style={{ textDecoration: 'none' }}
                >
                  <div 
                    className="property-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="property-image">
                      <img src={property.image} alt={property.title} />
                      <div className="property-badge">{property.category}</div>
                    </div>

                    <div className="property-content">
                      <h3>{property.title}</h3>
                      
                      <div className="property-details">
                        <div className="detail-item">
                          <FaMapMarkerAlt className="detail-icon" />
                          <span>{property.location}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaHome className="detail-icon" />
                          <span>{property.type}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaRuler className="detail-icon" />
                          <span>{property.area}</span>
                        </div>
                        
                        <div className="detail-item">
                          <FaLayerGroup className="detail-icon" />
                          <span>{property.floors}</span>
                        </div>
                      </div>

                      <div className="property-footer">
                        <div className="price-tag">Price on Request</div>
                        <button className="contact-btn">View Details</button>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            <button 
              className="slider-btn slider-btn-right" 
              onClick={() => scroll('right')}
              aria-label="Scroll Right"
            >
              <FaChevronRight />
            </button>
          </div>

          {/* View All Button */}
          <div className="view-all-wrapper">
            <Link to="/contact">
              <button className="view-all-btn">Contact Us</button>
            </Link>
          </div>

        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="contact-cta-section">
        <div className="cta-content">
          <h2>Looking for Your Dream Farmhouse or Villa?</h2>
          <p>Connect with our luxury property specialists today</p>
          <Link to="/contact" className="cta-link-btn">
            Get In Touch
          </Link>
        </div>
      </section>
    </>
  );
}
